import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Icon, IconButton, useMediaQuery, useTheme } from '@/ui';

import type { Company } from '@/types';
import type { FC, ReactNode } from 'react';

import About from '@/components/Company/Info/About';
import CompanyCulture from '@/components/Company/Info/Culture';
import CompanyGoals from '@/components/Company/Info/Goals';
import MissionAndVision from '@/components/Company/Info/Vision';

interface CompanyInfoPanelProps {
  company: Company;
  editDialog: ReactNode;
  isCollapsed?: boolean;
  onCollapsedChange?: (isCollapsed: boolean) => void;
  onEdit: () => void;
}

const CompanyInfoPanel: FC<CompanyInfoPanelProps> = ({
  company,
  editDialog,
  isCollapsed: controlledIsCollapsed,
  onCollapsedChange,
  onEdit,
}) => {
  const [uncontrolledIsCollapsed, setUncontrolledIsCollapsed] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'), {
    noSsr: true,
  });
  const isCollapsed = controlledIsCollapsed ?? uncontrolledIsCollapsed;
  const collapseButtonRef = useRef<HTMLButtonElement>(null);
  const expandButtonRef = useRef<HTMLButtonElement>(null);
  const previousIsCollapsed = useRef(isCollapsed);
  const shouldRestoreFocus = useRef(false);

  const setIsCollapsed = useCallback(
    (nextIsCollapsed: boolean) => {
      if (controlledIsCollapsed === undefined) {
        setUncontrolledIsCollapsed(nextIsCollapsed);
      }
      onCollapsedChange?.(nextIsCollapsed);
    },
    [controlledIsCollapsed, onCollapsedChange],
  );

  useEffect(() => {
    if (!isDesktop && isCollapsed) {
      setIsCollapsed(false);
    }
  }, [isCollapsed, isDesktop, setIsCollapsed]);

  useEffect(() => {
    if (previousIsCollapsed.current === isCollapsed) {
      return;
    }

    previousIsCollapsed.current = isCollapsed;
    if (shouldRestoreFocus.current) {
      (isCollapsed ? expandButtonRef : collapseButtonRef).current?.focus();
      shouldRestoreFocus.current = false;
    }
  }, [isCollapsed]);

  const toggleCollapsed = () => {
    const nextIsCollapsed = !isCollapsed;
    shouldRestoreFocus.current = true;
    setIsCollapsed(nextIsCollapsed);

    // Clears the pending focus restore when a controlled parent declines the
    // toggle. This relies on the parent's state update having been flushed
    // before the microtask runs, which holds for discrete click handlers. A
    // parent that defers the change (startTransition, async dispatch) would
    // need to drive focus from the click instead.
    queueMicrotask(() => {
      if (previousIsCollapsed.current === isCollapsed) {
        shouldRestoreFocus.current = false;
      }
    });
  };

  if (isCollapsed) {
    return (
      <div className="relative w-full bg-sage-100 lg:min-w-[58px] lg:max-w-[58px]">
        <IconButton
          ref={expandButtonRef}
          aria-label="Expand company panel"
          className="absolute left-2 top-[3px] hidden lg:!inline-flex"
          color="primary"
          size="large"
          onClick={toggleCollapsed}
        >
          <Icon>left_panel_open</Icon>
        </IconButton>
      </div>
    );
  }

  return (
    <div className="relative w-full bg-sage-100 px-8 py-4 lg:min-w-98 lg:max-w-98">
      <IconButton
        ref={collapseButtonRef}
        aria-label="Collapse company panel"
        className="absolute right-2 top-[3px] hidden lg:!inline-flex"
        color="primary"
        size="large"
        onClick={toggleCollapsed}
      >
        <Icon>left_panel_close</Icon>
      </IconButton>
      <About
        company={company}
        titleAction={
          <IconButton
            aria-label="edit"
            color="primary"
            size="large"
            onClick={onEdit}
          >
            <Icon>edit</Icon>
          </IconButton>
        }
      />
      <div
        className={clsx('flex flex-col gap-6', {
          'mt-16':
            !!company.vision || !!company.culture || !!company.goals.length,
        })}
      >
        {!!company.vision && <MissionAndVision vision={company.vision} />}
        {!!company.culture && <CompanyCulture culture={company.culture} />}
        {!!company.goals.length && <CompanyGoals goals={company.goals} />}
      </div>
      <div className="mt-4 flex gap-2">
        {!!company.linkedin && (
          <a href={company.linkedin} rel="noopener noreferrer" target="_blank">
            <IconButton color="primary" variant="outlined">
              <Icon>linkedin</Icon>
            </IconButton>
          </a>
        )}
        {!!company.website && (
          <a href={company.website} rel="noopener noreferrer" target="_blank">
            <IconButton color="primary" variant="outlined">
              <Icon>language</Icon>
            </IconButton>
          </a>
        )}
      </div>
      {editDialog}
    </div>
  );
};

export default CompanyInfoPanel;
